import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IProductGroup, IProductsReducer } from '../../types/product'
import { fetchProductsGroups } from '../async-thunks/products/groups/fetchProductsGroups'
import { fetchProducts } from '../async-thunks/products/fetchProducts'
import { ProductEntity, ProductStatus } from '../../entity/Product'
import { deleteOneProduct } from '../async-thunks/products/deleteOneProduct'
import { renameProduct } from '../async-thunks/products/renameProduct'
import { fetchQuickAccessProducts } from '../async-thunks/products/quickAccess/fetchQuickAccessProducts'
import { quickAccessAddProduct } from '../async-thunks/products/quickAccess/quickAccessAddProduct'
import { changeGroupsPositions } from '../async-thunks/products/groups/changeGroupsPositions'
import { editProductsGroup } from '../async-thunks/products/groups/editProductsGroup'
import { dndProductBetweenGroups } from '../async-thunks/products/groups/dndProductBetweenGroups'
import { quickAccessRemoveProduct } from '../async-thunks/products/quickAccess/quickAccessRemoveProduct'

const initialState: IProductsReducer = {
    products: [],
    quickAccess: [],
    productsGroups: [],
    isLoading: false,
    error: null,
    isProductRouted: false,
    productsIdDisplayNameDictionary: {},
    productsIdTypeDictionary: {},
    productLoading: '',
}

const productsSlice = createSlice({
    name: 'productsSlice',
    initialState,
    reducers: {
        cancelFetchProducts: (state) => {
            state.isLoading = false
        },
        changeProductStatus: (
            state,
            action: PayloadAction<{ guid: string; status: ProductStatus }>
        ) => {
            state.products = state.products.map((product) => {
                if (product.guid === action.payload.guid) {
                    return {...product, status: action.payload.status }
                }
                return product
            })
        },
        setIsProductRouted: (
            state,
            action: PayloadAction<{
                isProductRouted: boolean
            }>
        ) => {
            state.isProductRouted = action.payload.isProductRouted
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProducts.pending, (state, action) => {
                if (action.meta.arg.isInitialFetch) {
                    state.isLoading = true
                }
            })
            .addCase(fetchProducts.fulfilled, (state, action) => {
                const products = action.payload.products.map(
                    (product) => new ProductEntity(product)
                )

                const productsIdDisplayNameDictionary = products.reduce(
                    (acc: Record<string, any>, product: ProductEntity) => {
                        acc[product.guid] = product.display_name
                        return acc
                    },
                    {}
                )

                const productsIdTypeDictionary = products.reduce(
                    (acc: Record<string, any>, product: ProductEntity) => {
                        acc[product.guid] = product.type
                        return acc
                    },
                    {}
                )

                state.isLoading = false
                state.error = null
                state.products = products
                state.productsIdTypeDictionary = productsIdTypeDictionary
                state.productsIdDisplayNameDictionary =
                    productsIdDisplayNameDictionary
            })
            .addCase(fetchProducts.rejected, (state) => {
                state.isLoading = false
                state.error = 'Fetching error'
            })
            .addCase(
                deleteOneProduct.fulfilled,
                (state, action: PayloadAction<{ guid: string }>) => {
                    state.products = state.products.filter(
                        (product) => product.guid !== action.payload?.guid
                    )
                }
            )
            .addCase(renameProduct.pending, (state, action) => {
                state.productLoading = action.meta.arg.guid
            })
            .addCase(renameProduct.fulfilled, (state) => {
                state.productLoading = ''
            })
            .addCase(renameProduct.rejected, (state) => {
                state.productLoading = ''
            })
            .addCase(fetchQuickAccessProducts.fulfilled, (state, action) => {
                state.quickAccess = action.payload.quickAccessProductsIds
            })
            .addCase(quickAccessAddProduct.fulfilled, (state, action) => {
                state.quickAccess.push(action.payload.guid)
            })
            .addCase(quickAccessRemoveProduct.fulfilled, (state, action) => {
                state.quickAccess = state.quickAccess.filter(
                    (guid) => guid !== action.payload.guid
                )
            })
            .addCase(changeGroupsPositions.pending, (state, action) => {
                state.productsGroups = action.meta.arg.reorderedGroups
            })
            .addCase(editProductsGroup.pending, (state, action) => {
                state.productsGroups = state.productsGroups.map((group) => {
                    if (group.group_id === action.meta.arg.groupId) {
                        return {
                            ...group,
                            entries: action.meta.arg.data.entries,
                        }
                    }
                    return group
                })
            })
            .addCase(dndProductBetweenGroups.pending, (state, action) => {
                state.productsGroups = action.meta.arg.reorderedGroups
            })
            .addCase(
                fetchProductsGroups.fulfilled,
                (
                    state,
                    action: PayloadAction<{ productsGroups: IProductGroup[] }>
                ) => {
                    state.productsGroups = action.payload.productsGroups
                }
            )
    },
})

export const { actions: productsActions } = productsSlice
export const { reducer: productsReducer } = productsSlice
